import { CRMChannel } from "@jugl-web/rest-api";
import { TicketInboxFilter } from "@jugl-web/rest-api/tickets/models/common-types/TicketInboxFilter";
import {
  CRM_INBOX_PREFERENCES_KEY,
  getStorageItemWithFallback,
} from "@jugl-web/utils/storage";
import { TICKETS_FILTER_IDS } from "@jugl-web/domain-resources/tickets";
import { DEFAULT_TICKETS_VIEW, DEFAULT_CHANNEL_VIEW } from "./consts";
import {
  ChannelListItem,
  ClientSupportView,
  EntityInboxPreferences,
  InboxPreferencesByEntityId,
} from "./types";

export const isTicketFilterId = (value: string): value is TicketInboxFilter => {
  return TICKETS_FILTER_IDS.includes(value as TicketInboxFilter);
};

export const isCRMChannel = (value: string): value is CRMChannel => {
  return Object.values(CRMChannel).includes(value as CRMChannel);
};

export const isClientSupportViewsEqual = (
  a: ClientSupportView,
  b: ClientSupportView
): boolean => {
  if (a.kind !== b.kind) {
    return false;
  }

  if (a.kind === "channel" && b.kind === "channel") {
    return a.channel === b.channel;
  }

  if (a.kind === "tickets" && b.kind === "tickets") {
    return a.filter === b.filter;
  }

  return false;
};

export const getChannelFromView = (
  view: ClientSupportView
): CRMChannel | undefined => {
  return view.kind === "channel" ? view.channel : undefined;
};

export const resolveViewFromEntityPreferences = (
  preferences: EntityInboxPreferences | undefined,
  availableChannels: ChannelListItem[]
): ClientSupportView | undefined => {
  if (!preferences || !preferences.lastView) {
    return undefined;
  }

  const { lastView } = preferences;

  if (lastView.kind === "tickets") {
    return lastView;
  }

  const isChannelAvailable = availableChannels.some(
    (channel) => channel.id === lastView.channel
  );

  if (!isChannelAvailable && availableChannels.length > 0) {
    return undefined;
  }

  return lastView;
};

const getSavedInboxView = (
  entityId: string | undefined,
  availableChannels: ChannelListItem[]
): ClientSupportView | undefined => {
  if (!entityId) {
    return undefined;
  }

  const inboxPreferences =
    getStorageItemWithFallback<InboxPreferencesByEntityId>(
      CRM_INBOX_PREFERENCES_KEY,
      {},
      localStorage
    );

  return resolveViewFromEntityPreferences(
    inboxPreferences[entityId],
    availableChannels
  );
};

export const resolveInitialClientSupportView = (params: {
  urlChannel?: string;
  urlFilter?: string;
  entityId?: string;
  availableChannels: ChannelListItem[];
}): ClientSupportView => {
  const { urlChannel, urlFilter, entityId, availableChannels } = params;

  if (urlFilter) {
    const isValid = isTicketFilterId(urlFilter);

    return isValid
      ? { kind: "tickets", filter: urlFilter }
      : DEFAULT_TICKETS_VIEW;
  }

  if (urlChannel) {
    const isValid = isCRMChannel(urlChannel);

    return isValid
      ? { kind: "channel", channel: urlChannel }
      : DEFAULT_CHANNEL_VIEW;
  }

  return getSavedInboxView(entityId, availableChannels) ?? DEFAULT_CHANNEL_VIEW;
};

export type ClientSupportPathParams = {
  channel?: CRMChannel;
  activeConversation?: string;
};

const CLIENT_SUPPORT_BASE_PATH = "/client-support";

export const getClientSupportPathParamsFromPathname = (
  pathname: string
): ClientSupportPathParams => {
  const clientSupportIndex = pathname.indexOf(CLIENT_SUPPORT_BASE_PATH);

  if (clientSupportIndex === -1) {
    return {};
  }

  const segments = pathname
    .slice(clientSupportIndex + CLIENT_SUPPORT_BASE_PATH.length)
    .split("/")
    .filter(Boolean);

  const [firstSegment, secondSegment] = segments;

  if (!firstSegment || firstSegment === "tickets") {
    return {};
  }

  if (!isCRMChannel(firstSegment)) {
    return {};
  }

  if (secondSegment) {
    return {
      channel: firstSegment,
      activeConversation: secondSegment,
    };
  }

  return { channel: firstSegment };
};
